"use client";
import { Track } from "@/src/lib/tracking";

import { useCallback } from "react";
import { useOpenChatModal } from "@/src/hooks/useOpenChatModal";
import { useChatRecommendQuestion } from "@hooks/useChatRecommendQuestion";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { useIsPc } from "@/src/hooks/useIsPc";
import type { IOnCreateParams } from "@/src/hooks/useCreateChat";
import { openChatTabWithPendingEntry } from "@utils/chatUtils/chatTabEntry";
import { tryDelegateToNativeChat } from "@utils/chatUtils/tryDelegateToNativeChat";

export type OpenResponsiveChatParams = Pick<
  IOnCreateParams,
  | "question"
  | "autoSubmit"
  | "autoRenew"
  | "cacheQuestion"
  | "sign"
  | "tpl"
  | "secondOpinion"
>;

/**
 * 按视口分派 Chat 打开方式：
 * - **App + nativeChat**：`tryDelegateToNativeChat` → 原生 EdChat
 * - **大屏（PC）**：`openChatTabWithPendingEntry` → `/chat` 全页
 * - **小屏**：`useOpenChatModal` 弹窗
 */
export function useResponsiveChatOpen() {
  const router = useShellRouter();
  const handleOpenChatModal = useOpenChatModal();
  const [isPC] = useIsPc();
  const { questions: chatQuestion } = useChatRecommendQuestion();

  const openResponsiveChat = useCallback(
    (params?: OpenResponsiveChatParams) => {
      void (async () => {
        const {
          question,
          autoRenew = true,
          autoSubmit = true,
          ...rest
        } = params || {};

        const delegated = await tryDelegateToNativeChat({
          question: question ?? "",
          autoSubmit,
          autoRenew,
        });
        if (delegated) {
          Track.log("info", "ResponsiveChat", "delegated to native chat", {
            autoRenew,
            autoSubmit,
          });
          return;
        }

        const chatParams = {
          question,
          autoRenew,
          autoSubmit,
          questions: chatQuestion?.slice(0, 4) || [],
          ...rest,
        };

        if (isPC) {
          Track.log(
            "info",
            "ResponsiveChat",
            "navigate chat tab with pending entry",
            {
              autoRenew,
              autoSubmit,
            },
          );
          openChatTabWithPendingEntry(router, chatParams);
          return;
        }

        Track.log("info", "ResponsiveChat", "open chat modal", {
          autoRenew,
          autoSubmit,
        });
        void handleOpenChatModal(chatParams);
      })();
    },
    [chatQuestion, handleOpenChatModal, isPC, router],
  );

  return { openResponsiveChat };
}
